/*
 * KR Maps — Make your map: the one style table for every builder of the Desk Size world map.
 *
 * Loaded before assets/kr-make-your-map.js by the listing (snippets/kr-make-your-map.liquid), and by the
 * print files and the Etsy proof desk, which load both files from the theme repo's main branch rather than
 * keeping copies. kr-make-your-map.js looks this table up each time it draws, laying it over its own
 * built-in defaults, so a fix here reaches the listing, the prints and the Etsy proofs at once.
 *
 * styles.<style>.inks — colours, read off each style PSD's own composite (Dark Forest's include its 15 %
 *   multiply wash). Keys: paper (the map's margin, shown while the basemap loads), title, sub (subtitle),
 *   divider (the rule under the title), diamond (its diamond; default = divider), legend (legend words),
 *   ring (legend rings), rule (the rule split by the compass), vline (the legend's divider; default = rule),
 *   compass (default = rule), names, date.
 *
 * styles.<style>.layout — optional per-style overrides of the layout defaults in kr-make-your-map.js, in the
 *   PSD's own pixels (12000 x 9600 = 20 x 16 in at 600 ppi). Only what differs from the defaults goes here;
 *   nested keys merge one by one. Blocks and their defaults:
 *     TYPE.title / subtitle / legend / names / date: { size (px), track (em), dx (px off the centre line), fit (max px),
 *       vscale (vertical scale; every PSD type layer is at 1.04) }
 *       title { size: 517, track: 0.165, dx: 22, fit: 10600 }, subtitle { size: 150, track: 0.2 },
 *       legend { size: 108.33, track: 0.2 }, names { size: 275, track: 0.3 }, date { size: 150, track: 0.2 }
 *     TOP.full { title: 983, divider: 1263, subtitle: 1648 }    baselines and the divider's centre, with a subtitle
 *     TOP.alone { title: 1082, divider: 1479.5 }                the same without one
 *     DIVIDER { cx: 6012, half: 1551.5, stroke: 13, dw: 63, dh: 63.5 }
 *     LEGEND { cx: 5958, vUp: 360, vDown: 18, hugL: 323, hugR: 237, gap3: 740, ring: 163.5, ringStroke: 20.3,
 *              gapText: 80, gapTexts: [item 1, item 2, item 3] }
 *       vUp: how far the 2-item divider rises above the legend's centre line (the stack follows it)
 *       gapTexts: ring-to-words gap per item, where a style's items differ (falls back to gapText)
 *     RULE { cx: 5962, half: 1813, gap: 252, w: 9 }
 *     STACK { legend2|legend3|compass|names|date: { c, up, down } }   centres of the full bottom block
 *     GAP { legend_compass: 238, compass_names: 317.5, names_date: 339.5, compass_date: 300 }
 *
 * Kevin, 16 Oct 2026: the inks stay as they are; they equal his PSDs. "Follow Megan's spacing from the recent
 * orders": TOP is each style's median over the last 90 days of order PSDs (E:\KRMapsDesk\style-audit\spacing.json),
 * and the title tracking matches her orders' letter spacing (her Playfair spaces every gap 0.045 em wider than
 * the web font at the same settings; the letters themselves are identical). The rest is each style master's own,
 * from the PSD layer records (E:\KRMapsDesk\style-audit\specs.md): Sage's title rule sits 60 px left; Navy and
 * Terracotta use their own template (a bigger, wider-tracked title, a taller legend divider, and on Navy a longer
 * rule and a wider gap before the second legend item).
 */
window.KRMymStyles = {
  version: 1,
  styles: {
    forest: {
      inks: { paper: '#95956d', title: '#fbeed9', sub: '#efce8b', divider: '#fbeed9', legend: '#fbeed9', ring: '#efce8b', rule: '#efce8b', names: '#fbeed9', date: '#efce8b' },
      layout: {
        TOP: { full: { title: 998, divider: 1275, subtitle: 1656 }, alone: { title: 1090, divider: 1499.5 } }   // n 12-14; alone pooled
      }
    },
    darkforest: {
      inks: { paper: '#525237', title: '#eedec7', sub: '#e2c080', divider: '#eedec7', legend: '#eedec7', ring: '#e2c080', rule: '#e2c080', names: '#eedec7', date: '#e2c080' },
      layout: {
        TOP: { full: { title: 1018, divider: 1303.5, subtitle: 1670 }, alone: { title: 1127, divider: 1551.5 } } // n 16-21 / 11-12
      }
    },
    sage: {
      inks: { paper: '#f5ebd9', title: '#2f3a2f', sub: '#c2a46d', divider: '#5f6b5c', legend: '#2f3a2f', ring: '#aa8a3c', rule: '#5f6b5c', names: '#2f3a2f', date: '#c2a46d' },
      layout: {
        TOP: { full: { title: 993, divider: 1271.5, subtitle: 1660.5 }, alone: { title: 1079, divider: 1495.5 } }, // n 65-81 / 18
        DIVIDER: { cx: 5952 }                                   // line 4402-7507, diamond 5889-6015
      }
    },
    navy: {
      inks: { paper: '#587884', title: '#f2e9da', sub: '#d7b45e', divider: '#d7b45e', legend: '#e5d9cb', ring: '#d7b45e', rule: '#d7b45e', names: '#f2e9da', date: '#d7b45e' },
      layout: {
        TYPE: { title: { size: 533.33, track: 0.245, dx: -15 }, subtitle: { size: 166.67 } },   // PSD: 533.33 px, tracking 200 (+0.045)
        TOP: { full: { title: 997, divider: 1306.5, subtitle: 1705 }, alone: { title: 1101, divider: 1522.5 } }, // n 30-35 / 15-19
        DIVIDER: { cx: 5998, half: 1553 },                      // line 4447-7553, diamond 5935-6061
        LEGEND: { vUp: 829, gapTexts: [80, 140.5] },            // divider 7231-8042; second item's words start at 6499
        RULE: { half: 2300 }                                    // 3668-8268, split by the compass like the others
      }
    },
    terracotta: {
      inks: { paper: '#f8f1e2', title: '#414449', sub: '#ce633d', divider: '#aa8a3c', legend: '#414449', ring: '#ce633d', rule: '#aa8a3c', names: '#414449', date: '#ce633d' },
      layout: {
        TYPE: { title: { size: 533.33, track: 0.285, dx: 7 } },  // PSD: 533.33 px, tracking 240 (+0.045)
        TOP: { full: { title: 1008, divider: 1290.5, subtitle: 1667.5 } },   // n 13-14; too few without a subtitle to set alone
        DIVIDER: { cx: 5998, half: 1552.5 },                    // line 4448-7553, diamond 5935-6061
        LEGEND: { vUp: 829 }                                    // divider 7231-8042
      }
    },
    neutral: {
      inks: { paper: '#fdf2ea', title: '#5a554a', sub: '#ae927a', divider: '#7a7468', legend: '#414449', ring: '#ae927a', rule: '#7a7468', names: '#5a554a', date: '#ae927a' },
      layout: {
        TOP: { full: { title: 983, divider: 1263, subtitle: 1648 }, alone: { title: 1082, divider: 1479.5 } }    // n 56-63 / 12
      }
    },
    /* The April 2026 master (Z:\MastersKevin\DeskSize\April2026Master_2.psd), the push-pin world map with pins:
       its own layout (kind april: a title with one word in Dancing Script on a band that breaks the border, names,
       a date or message). Its layout numbers are the defaults in kr-make-your-map.js (APRIL); overrides go here. */
    april: {
      kind: 'april',
      inks: { paper: '#f8f5f0', title: '#334155', script: '#334155', names: '#334155', date: '#334155', border: '#3e4046' }
    },
    /* The Muted Tones USA master (Z:\MastersMegan\Muted_Tones_USA.psd, 10800 x 7200): its own board and layout (kind
       badge: names on a ribbon, a date, up to five legend lines or a quote, in Bebas Kai). badge: the layout groups'
       colour overlay (drawn at their 80 %); panel: the paper panel under the frame. Its layout numbers are the defaults
       in kr-make-your-map.js (BADGE), the PSD's own; overrides go here, per layout (names, one ... five, quote), and
       at: [dx, dy] moves a layout's art, panel and lines together. */
    mutedusa: {
      kind: 'badge',
      inks: { paper: '#f7f1eb', panel: '#f7f1eb', badge: '#406174' },
      layout: {
        /* Kevin, 17 Oct 2026. Of the five-line badge: "jumps over here after i add 5 lines". The master parks that group
           on the lower left, over the Pacific; it sits with the others in the Gulf, centred where the four-line badge is
           (x 7213.5) with its top where the three-line one's is (5710), so it ends where the four-line one does (6850).
           And: "I add a background color rectangle layer normally in order to block out the graticles behind the legend".
           The master has that paper panel (its 'Rectangle 1') under one, two and three lines; four and five get the same,
           the frame's own box as those are (read off the art: within 4 px of the master's on every side). Five's is in
           the master's place and moves with it. */
        LAYOUTS: {
          four: { panel: [6800, 5854, 7630, 6850] },
          five: { at: [5038, 985], panel: [1784, 4833, 2569, 5866] }
        }
      }
    },
    /* The Muted Tones world master (Z:\MastersMegan\Muted_Tones_24x36_MasterNew.psd, 10800 x 7200, the one Megan's world
       orders are made from): the same badge, its layouts set together on the lower left over the South Pacific, where
       no grid line crosses, so no paper panels. Its layout numbers are BADGE.WORLD in kr-make-your-map.js; overrides go
       here as for mutedusa. */
    mutedworld: {
      kind: 'badge',
      inks: { paper: '#f6f1eb', panel: '#f7f1eb', badge: '#406174' }
    }
  }
};
